import React, { useState, useEffect, useRef } from 'react';
import { PostRequest } from 'components/common/index.js';
import styles from 'components/quote_builder_widget/shared/_customer_popup.module.scss';
import { joinClassNames } from 'components/quote_builder_widget/shared/customerPopupClassNames';

const CODE_LENGTH = 6;

// The customer's 2FA (QuoteBuilderWidget::CustomerConfirmationService), shown once a quote request is submitted: the
// right code sends the request, a request that then fails to become a quote goes back to the form with its error.
// classNames: the theme's customerPopupClassNames, as CustomerContactPopup takes them.
const CustomerConfirmationPopup = ({ widgetIdentifier, confirmation, onConfirmed, onQuoteError, onClose, classNames }) => {
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [notice, setNotice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // The code was invalidated (too many wrong codes, or it expired): only a new one can be confirmed.
  const [codeInvalid, setCodeInvalid] = useState(false);
  // The request itself expired: the form has to be submitted again.
  const [requestExpired, setRequestExpired] = useState(false);
  const [secondsUntilResend, setSecondsUntilResend] = useState(confirmation.resendAvailableInSeconds || 0);
  const codeInputRef = useRef(null);

  useEffect(() => {
    codeInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (secondsUntilResend <= 0) {
      return undefined;
    }
    const timeout = setTimeout(() => setSecondsUntilResend((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timeout);
  }, [secondsUntilResend]);

  const showError = (data) => {
    setNotice(null);
    setErrorMessage(data?.error || t('quote_builder_widget.unexpected_error'));
  };

  // The service answers its own limits with JSON; the per-IP throttle (rack-attack) answers in plain text.
  const showTooManyAttempts = (data) => {
    showError(data?.error ? data : { error: t('quote_builder_widget.customer_confirmation_too_many_attempts') });
  };

  const confirm = (event) => {
    event.preventDefault();
    if (submitting || code.length !== CODE_LENGTH || codeInvalid || requestExpired) {
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);
    const failed = (handler) => (data) => {
      setSubmitting(false);
      handler(data);
    };
    PostRequest({
      url: `/widget/quote-builder/${widgetIdentifier}/confirm-customer-code`,
      data: { pending_confirmation_token: confirmation.pendingConfirmationToken, otp_code: code },
      keysLeftOutOfTracking: ['pending_confirmation_token', 'otp_code'],
      callback: (data) => {
        setSubmitting(false);
        onConfirmed(data);
      },
      422: failed((data) => {
        showError(data);
        setCode('');
        if (data?.status === 'locked' || data?.status === 'expired') {
          setCodeInvalid(true);
        }
      }),
      400: failed(onQuoteError),
      403: failed(onQuoteError),
      404: failed(onQuoteError),
      429: failed(showTooManyAttempts),
      503: failed(showError),
      500: failed(showError),
    }).catch(() => setSubmitting(false));
  };

  const resend = () => {
    if (secondsUntilResend > 0 || requestExpired) {
      return;
    }

    setErrorMessage(null);
    PostRequest({
      url: `/widget/quote-builder/${widgetIdentifier}/resend-customer-code`,
      data: { pending_confirmation_token: confirmation.pendingConfirmationToken },
      keysLeftOutOfTracking: ['pending_confirmation_token'],
      callback: (data) => {
        setCode('');
        setCodeInvalid(false);
        setNotice(t('quote_builder_widget.customer_confirmation_resent'));
        setSecondsUntilResend(data.resend_available_in_seconds || 0);
        codeInputRef.current?.focus();
      },
      422: (data) => {
        showError(data);
        setRequestExpired(true);
      },
      429: (data) => {
        showTooManyAttempts(data);
        setSecondsUntilResend(data?.resend_available_in_seconds || 0);
      },
      503: showError,
      500: showError,
    }).catch(() => {});
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  const sentMessageKey = confirmation.customerTwoFactor === 'sms'
    ? 'quote_builder_widget.customer_confirmation_sent_by_sms'
    : 'quote_builder_widget.customer_confirmation_sent_by_email';

  return (
    <div className={styles.overlay} data-widget-element="customer-confirmation-popup" onKeyDown={onKeyDown}>
      <form
        className={joinClassNames(styles.card, classNames.card)}
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-confirmation-title"
        onSubmit={confirm}
        data-widget-element="customer-confirmation-card"
      >
        <h4 id="customer-confirmation-title" className={styles.title} data-widget-element="customer-confirmation-title">
          {t('quote_builder_widget.customer_confirmation_title')}
        </h4>
        <p className={joinClassNames(styles.text, classNames.text)} data-widget-element="customer-confirmation-text">
          {t(sentMessageKey, { destination: confirmation.customerDestination })}
        </p>
        <input
          ref={codeInputRef}
          className={joinClassNames(styles.codeInput, classNames.input)}
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LENGTH}
          aria-label={t('quote_builder_widget.customer_confirmation_code')}
          placeholder={t('quote_builder_widget.customer_confirmation_code')}
          disabled={codeInvalid || requestExpired}
          data-widget-element="customer-confirmation-code"
        />
        {errorMessage && (
          <div className={joinClassNames(styles.message, classNames.error)} role="alert" data-widget-element="customer-confirmation-error">{errorMessage}</div>
        )}
        {notice && !errorMessage && (
          <div className={joinClassNames(styles.message, classNames.notice)} role="status" data-widget-element="customer-confirmation-notice">{notice}</div>
        )}
        <div className={styles.buttonRow}>
          <button
            type="submit"
            className={classNames.button}
            disabled={submitting || code.length !== CODE_LENGTH || codeInvalid || requestExpired}
            data-widget-element="customer-confirmation-confirm"
          >
            {t('quote_builder_widget.customer_confirmation_confirm')}
          </button>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={joinClassNames(styles.linkButton, classNames.link)}
            onClick={resend}
            disabled={secondsUntilResend > 0 || requestExpired}
            data-widget-element="customer-confirmation-resend"
          >
            {secondsUntilResend > 0
              ? t('quote_builder_widget.customer_confirmation_resend_in', { seconds: secondsUntilResend })
              : t('quote_builder_widget.customer_confirmation_resend')}
          </button>
          <button type="button" className={joinClassNames(styles.linkButton, classNames.link)} onClick={onClose} data-widget-element="customer-confirmation-cancel">
            {t('quote_builder_widget.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerConfirmationPopup;
